/**
 * CelesTrak GP queries for the satellite catalogue and the live TLE refresh,
 * ported from loader-catalog.js (searchByName, loadGroup, refreshLiveTLEs,
 * addSatellitesToGlobe). Requests go through the Worker proxy (/api/loader/celestrak,
 * edge-cached for 2 h) rather than to celestrak.org directly as the original did:
 * CelesTrak blocks addresses that query it too often, and the proxy keeps one
 * cached copy per group. Group results are also cached in memory for 2 h.
 */
import { CELESTRAK, EARTH, LOADER_API_BASE } from '../constants';
import { deriveOrbitFromTLE, ommToTle, tleEpochAgeDays, type OmmRecord } from '../engine';
import type { Mission, Tle } from '../types';

export type { OmmRecord };

/** 20 distinct colours for catalogue satellites, as the original palette. */
export const CATALOG_PALETTE = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#C9CBCF',
  '#7BC8A4',
  '#E7549A',
  '#5AD5D5',
  '#FDB45C',
  '#949FB1',
  '#8B5CF6',
  '#F97316',
  '#06B6D4',
  '#EC4899',
  '#84CC16',
  '#F43F5E',
  '#0EA5E9',
  '#A855F7',
];

export const CATALOG_ID_PREFIX = 'catalog_';
export const CATALOG_TIER = 9;
/** Group results are reused for this long (CELESTRAK.REFRESH_INTERVAL_MS, 2 h). */
export const GROUP_CACHE_MS = CELESTRAK.REFRESH_INTERVAL_MS;

const PROXY = `${LOADER_API_BASE}/celestrak`;

export function searchUrl(name: string): string {
  return `${PROXY}?name=${encodeURIComponent(name)}&format=json`;
}

export function groupUrl(group: string): string {
  return `${PROXY}?group=${encodeURIComponent(group)}&format=json`;
}

export function catnrUrl(noradId: number | string): string {
  return `${PROXY}?catnr=${encodeURIComponent(String(noradId))}&format=json`;
}

export type FetchJson = (url: string) => Promise<unknown>;

/**
 * Default transport: a plain fetch of the CelesTrak URL. CelesTrak answers an
 * empty query with the text "No GP data found" (status 200), which is returned
 * as an empty list; any other non-JSON body or non-OK status is an error.
 */
export const fetchCelestrakJson: FetchJson = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`CelesTrak responded ${res.status}`);
  const text = await res.text();
  try {
    return JSON.parse(text) as unknown;
  } catch (err) {
    if (/No GP data found/i.test(text)) return [];
    throw new Error(`CelesTrak returned an unreadable response (${text.slice(0, 60).trim()})`, {
      cause: err,
    });
  }
};

/** CelesTrak returns an array; a single object or anything else is normalised to a list. */
export function toRecords(data: unknown): OmmRecord[] {
  if (Array.isArray(data)) return data.filter((d): d is OmmRecord => typeof d === 'object' && d !== null);
  if (data && typeof data === 'object') return [data as OmmRecord];
  return [];
}

export interface CelestrakDeps {
  fetchJson?: FetchJson;
  now?: () => number;
}

interface GroupCacheEntry {
  data: OmmRecord[];
  timestamp: number;
}

const groupCache = new Map<string, GroupCacheEntry>();

export function clearGroupCache(): void {
  groupCache.clear();
}

/** Cached group records when they are younger than GROUP_CACHE_MS, else null. */
export function cachedGroup(group: string, nowMs: number): OmmRecord[] | null {
  const entry = groupCache.get(group);
  if (!entry) return null;
  if (nowMs - entry.timestamp >= GROUP_CACHE_MS) return null;
  return entry.data;
}

export async function searchByName(query: string, deps: CelestrakDeps = {}): Promise<OmmRecord[]> {
  const fetchJson = deps.fetchJson ?? fetchCelestrakJson;
  return toRecords(await fetchJson(searchUrl(query)));
}

export async function loadGroup(
  group: string,
  deps: CelestrakDeps = {},
): Promise<{ records: OmmRecord[]; fromCache: boolean }> {
  const now = deps.now ?? Date.now;
  const cached = cachedGroup(group, now());
  if (cached) return { records: cached, fromCache: true };
  const fetchJson = deps.fetchJson ?? fetchCelestrakJson;
  const records = toRecords(await fetchJson(groupUrl(group)));
  groupCache.set(group, { data: records, timestamp: now() });
  return { records, fromCache: false };
}

/** The GP record for one NORAD id, or null when CelesTrak has none. */
export async function fetchByCatnr(
  noradId: number | string,
  deps: CelestrakDeps = {},
): Promise<OmmRecord | null> {
  const fetchJson = deps.fetchJson ?? fetchCelestrakJson;
  const records = toRecords(await fetchJson(catnrUrl(noradId)));
  return records[0] ?? null;
}

// Field accessors: CelesTrak emits upper snake case; the camel-case variants are kept from the original.

export function ommNoradId(omm: OmmRecord): number {
  const raw = omm.NORAD_CAT_ID ?? omm.noradCatId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

function str(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

export function ommName(omm: OmmRecord): string {
  const name = str(omm.OBJECT_NAME) || str(omm.objectName);
  if (name) return name;
  const norad = ommNoradId(omm);
  return norad ? `SAT-${norad}` : 'Unknown';
}

export function ommMeanMotion(omm: OmmRecord): number {
  const n = Number(omm.MEAN_MOTION ?? omm.meanMotion);
  return Number.isFinite(n) ? n : 0;
}

export function ommInclination(omm: OmmRecord): number {
  const n = Number(omm.INCLINATION ?? omm.inclination);
  return Number.isFinite(n) ? n : 0;
}

export function ommEpoch(omm: OmmRecord): string {
  return str(omm.EPOCH) || str(omm.epoch);
}

/** TLE lines carried on the record (TLE_LINE1/2) or built from the OMM elements; null when neither works. */
export function ommTle(omm: OmmRecord): Tle | null {
  const line1 = str(omm.TLE_LINE1) || str(omm.tle_line1);
  const line2 = str(omm.TLE_LINE2) || str(omm.tle_line2);
  if (line1 && line2) return { line1, line2 };
  return ommToTle(omm);
}

export interface MeanMotionOrbit {
  /** Classified by altitude like the mission orbit: GEO above 35 000 km, MEO above 2 000 km, else LEO. */
  type: 'LEO' | 'MEO' | 'GEO';
  altKm: number | null;
  periodMin: number | null;
}

/** Altitude and period from the mean motion (revs/day), as the catalogue rows show them. */
export function orbitFromMeanMotion(meanMotion: number): MeanMotionOrbit {
  if (!(meanMotion > 0)) return { type: 'LEO', altKm: null, periodMin: null };
  const n = (meanMotion * 2 * Math.PI) / 86400;
  const a = Math.pow(EARTH.GM / (n * n), 1 / 3);
  const altKm = Math.round(a - EARTH.WGS84_A_KM);
  const periodMin = 1440 / meanMotion;
  return { type: orbitTypeForAltitude(altKm), altKm, periodMin };
}

export function orbitTypeForAltitude(altKm: number): 'LEO' | 'MEO' | 'GEO' {
  return altKm > 35000 ? 'GEO' : altKm > 2000 ? 'MEO' : 'LEO';
}

/** Age of an OMM epoch as the rows show it: hours with one decimal under a day, else whole days; '--' when unknown. */
export function epochAgeLabel(epoch: string, nowMs: number): string {
  if (!epoch) return '--';
  const epochMs = new Date(epoch.endsWith('Z') ? epoch : epoch + 'Z').getTime();
  if (Number.isNaN(epochMs)) return '--';
  const ageHrs = (nowMs - epochMs) / 3_600_000;
  return ageHrs < 24 ? `${ageHrs.toFixed(1)}h` : `${Math.round(ageHrs / 24)}d`;
}

export interface CatalogRow {
  noradId: number;
  name: string;
  orbitType: string;
  altKm: number | null;
  inclinationDeg: number;
  epochAge: string;
}

/** The display model of one list row. */
export function catalogRow(omm: OmmRecord, nowMs: number): CatalogRow {
  const orbit = orbitFromMeanMotion(ommMeanMotion(omm));
  return {
    noradId: ommNoradId(omm),
    name: ommName(omm),
    orbitType: orbit.type,
    altKm: orbit.altKm,
    inclinationDeg: ommInclination(omm),
    epochAge: epochAgeLabel(ommEpoch(omm), nowMs),
  };
}

export function catalogMissionId(noradId: number | string): string {
  return CATALOG_ID_PREFIX + String(noradId);
}

/**
 * A catalogue satellite as a loader Mission: id catalog_<norad>, isCatalog,
 * tier 9 'Catalog', S-band with no data requirement, orbit derived from the
 * TLE (type by altitude, period from the mean motion) with its epoch age.
 * Null when the record has no NORAD id or its TLE cannot be built.
 */
export function catalogMissionFromOmm(omm: OmmRecord, color: string, nowMs: number): Mission | null {
  const noradId = ommNoradId(omm);
  if (!noradId) return null;
  const tle = ommTle(omm);
  if (!tle) return null;
  const derived = deriveOrbitFromTLE(tle.line1, tle.line2);
  const name = ommName(omm);
  return {
    id: catalogMissionId(noradId),
    name,
    shortName: name.length > 10 ? name.substring(0, 10) : name,
    noradId,
    agency: 'CelesTrak Catalog',
    isCatalog: true,
    color,
    priorityTier: CATALOG_TIER,
    priorityLabel: 'Catalog',
    canBeBumped: true,
    preferredAntennas: [],
    acceptableAlternatives: [],
    orbit: {
      type: derived.type,
      altitudeKm: derived.altKm,
      inclinationDeg: derived.incDeg,
      periodMinutes: derived.periodMin,
      tle,
      tleEpochAgeDays: tleEpochAgeDays(tle.line1, nowMs),
    },
    requirements: {
      bandType: 'S-band',
      dataPerDayGB: 0,
      minutesPerYear: 0,
      minPassDurationMin: 1,
      slaDescription: 'Catalog satellite, visualization only',
    },
    description: `Loaded from CelesTrak satellite catalog (NORAD #${noradId})`,
  };
}
